import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, Check, Gift, Calendar, Presentation } from 'lucide-react';
import Navigation from '../components/Navigation';
import { CeleyLoader } from '../components/ui/CeleyLoader';

const PlatformSettings = () => {
  const [settings, setSettings] = useState(null);
  const [vendorAgreement, setVendorAgreement] = useState('');
  const [serviceAgreement, setServiceAgreement] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [error, setError] = useState('');
  // Phase 2: optional reason/note for Payment Settings audit log
  const [feeReason, setFeeReason] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSettings();
    fetchAgreements();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setError(''); // Clear any error
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to fetch settings:', response.status, errorData);
        
        // Set defaults so page loads
        setSettings({
          deposit_percentage: 15.0,
          platform_fee_percentage: 3.0,
          auto_release_hours: 48,
          promo_active: false,
          promo_start_date: '',
          promo_end_date: '',
          promo_fee_percentage: 0,
          promo_duration_days: 90
        });
        
        // Show specific error message
        if (response.status === 403) {
          setError('Admin access required. Please make sure you are logged in as an admin.');
        } else if (response.status === 401) {
          setError('Not authenticated. Please log in again.');
        } else {
          setError(`Failed to load settings (Error ${response.status}). Showing defaults.`);
        }
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      setSettings({
        deposit_percentage: 15.0,
        platform_fee_percentage: 3.0,
        auto_release_hours: 48,
        promo_active: false,
        promo_start_date: '',
        promo_end_date: '',
        promo_fee_percentage: 0,
        promo_duration_days: 90
      });
      setError('Failed to connect to server. Showing default settings.');
    }
  };

  const fetchAgreements = async () => {
    try {
      const [vendorResp, serviceResp] = await Promise.all([
        fetch(`${API_URL}/api/agreements/vendor_participation`),
        fetch(`${API_URL}/api/agreements/service_agreement`)
      ]);

      if (vendorResp.ok) {
        const data = await vendorResp.json();
        setVendorAgreement(data.content);
      }
      if (serviceResp.ok) {
        const data = await serviceResp.json();
        setServiceAgreement(data.content);
      }
    } catch (err) {
      console.error('Failed to fetch agreements:', err);
    }
  };

  const handleSaveSettings = async () => {
    // Phase 2: attach optional reason for the audit log; never persisted on the doc
    const payload = feeReason && feeReason.trim()
      ? { ...settings, reason: feeReason.trim() }
      : settings;
    await handleSave(payload);
    setFeeReason(''); // reset after save
  };

  const handleSave = async (settingsToSave) => {
    setLoading(true);
    setError('');
    setSaveMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform-settings`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settingsToSave)
      });

      if (!response.ok) throw new Error('Failed to save settings');
      
      const data = await response.json();
      setSettings(data);
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAgreement = async (type, content) => {
    setLoading(true);
    setError('');
    setSaveMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/agreements/${type}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });

      if (!response.ok) throw new Error('Failed to save agreement');
      
      setSaveMessage(`${type === 'vendor_participation' ? 'Vendor' : 'Service'} agreement saved successfully!`);
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!settings) {
    return (
      <div className="min-h-screen-safe bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <CeleyLoader inline label="Loading settings" testid="platform-settings-loader" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe bg-gray-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold mb-8">Platform Settings</h1>

        {saveMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg flex items-center">
            <Check size={20} className="mr-2" />
            {saveMessage}
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center">
            <AlertCircle size={20} className="mr-2" />
            {error}
          </div>
        )}

        {/* Platform Promo Status Reminder (Read-Only) */}
        <div className={`mb-6 p-4 rounded-lg border ${settings.promo_active ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <Gift size={18} className={settings.promo_active ? 'text-purple-600' : 'text-gray-400'} />
            {settings.promo_active ? (
              <p className="text-sm text-purple-800">
                <span className="font-medium">Platform Promo Active:</span>{' '}
                {settings.promo_fee_percentage || 0}% fees for new vendors
                {settings.promo_end_date && ` until ${settings.promo_end_date.split('T')[0]}`}
                {!settings.promo_end_date && settings.promo_duration_days && ` (${settings.promo_duration_days}-day waiver per vendor)`}
              </p>
            ) : (
              <p className="text-sm text-gray-500">No global platform promo active</p>
            )}
          </div>
        </div>

        {/* Presentation Mode Toggle */}
        <div className={`mb-6 p-5 rounded-lg border-2 ${settings.presentation_mode ? 'bg-amber-50 border-amber-400' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${settings.presentation_mode ? 'bg-amber-400 text-white' : 'bg-gray-200 text-gray-500'}`}>
                <Presentation size={20} />
              </div>
              <div>
                <h3 className="font-bold text-base text-gray-900">Presentation Mode</h3>
                <p className="text-sm text-gray-600">
                  {settings.presentation_mode
                    ? 'Active — Only curated vendors visible to customers'
                    : 'Off — All eligible vendors visible normally'}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                const newVal = !settings.presentation_mode;
                setSettings({ ...settings, presentation_mode: newVal });
                handleSave({ ...settings, presentation_mode: newVal });
              }}
              data-testid="presentation-mode-toggle"
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${settings.presentation_mode ? 'bg-amber-500' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${settings.presentation_mode ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {/* Payment Settings */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Payment Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deposit Percentage (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={settings.deposit_percentage}
                onChange={(e) => setSettings({ ...settings, deposit_percentage: parseFloat(e.target.value) })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                The percentage of the total price customers pay as a deposit
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Platform Fee Percentage (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={settings.platform_fee_percentage}
                onChange={(e) => setSettings({ ...settings, platform_fee_percentage: parseFloat(e.target.value) })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                The percentage deducted from vendor payouts as platform fee
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auto-Release Window (hours)
              </label>
              <input
                type="number"
                min="1"
                max="168"
                value={settings.auto_release_hours}
                onChange={(e) => setSettings({ ...settings, auto_release_hours: parseInt(e.target.value) })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Hours after vendor marks complete before payment auto-releases
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason / Note (optional, for audit log)
              </label>
              <input
                type="text"
                maxLength={500}
                value={feeReason}
                onChange={(e) => setFeeReason(e.target.value)}
                placeholder="e.g. Q2 launch — drop fee from 5% to 3%"
                data-testid="payment-settings-reason"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                If provided, the note is captured in the audit trail alongside who made the change.
              </p>
            </div>

            <button
              onClick={handleSaveSettings}
              disabled={loading}
              data-testid="save-payment-settings-btn"
              className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 font-medium disabled:bg-gray-300 flex items-center"
            >
              <Save size={18} className="mr-2" />
              {loading ? 'Saving...' : 'Save Payment Settings'}
            </button>
          </div>
        </div>

        {/* Promotions Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Gift className="text-purple-600" size={24} />
            <h2 className="text-xl font-semibold">Promotions</h2>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
            <h3 className="font-medium text-purple-900 mb-2">90-Day Platform Fee Waiver (Founding Vendors)</h3>
            <p className="text-sm text-purple-700">
              When active, new vendors who sign up during the eligibility window will automatically receive 
              a {settings.promo_duration_days || 90}-day platform fee waiver. Their fee will be {settings.promo_fee_percentage || 0}% 
              during this period, then revert to the standard {settings.platform_fee_percentage}%.
            </p>
          </div>
          
          <div className="space-y-4">
            {/* Promo Active Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block font-medium text-gray-900">Promo Active</label>
                <p className="text-sm text-gray-500">Enable automatic promo application for new vendors</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, promo_active: !settings.promo_active })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.promo_active ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.promo_active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Promo Eligibility Window */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar size={16} className="inline mr-1" />
                  Promo Start Date
                </label>
                <input
                  type="date"
                  value={settings.promo_start_date?.split('T')[0] || ''}
                  onChange={(e) => setSettings({ ...settings, promo_start_date: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">Vendors who sign up on or after this date are eligible</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar size={16} className="inline mr-1" />
                  Promo End Date
                </label>
                <input
                  type="date"
                  value={settings.promo_end_date?.split('T')[0] || ''}
                  onChange={(e) => setSettings({ ...settings, promo_end_date: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">Last day to sign up and get the promo</p>
              </div>
            </div>

            {/* Promo Fee and Duration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Promo Fee Percentage (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.promo_fee_percentage ?? 0}
                  onChange={(e) => setSettings({ ...settings, promo_fee_percentage: parseFloat(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">Platform fee during promo (usually 0%)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Promo Duration (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={settings.promo_duration_days ?? 90}
                  onChange={(e) => setSettings({ ...settings, promo_duration_days: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">How long each vendor&apos;s promo lasts</p>
              </div>
            </div>

            {/* Status Summary */}
            {settings.promo_active && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="font-medium text-green-800">
                  ✓ Promo is ACTIVE
                </p>
                <p className="text-sm text-green-700 mt-1">
                  New vendors signing up 
                  {settings.promo_start_date && ` from ${settings.promo_start_date.split('T')[0]}`}
                  {settings.promo_end_date && ` until ${settings.promo_end_date.split('T')[0]}`}
                  {' '}will get {settings.promo_fee_percentage || 0}% platform fee for {settings.promo_duration_days || 90} days.
                </p>
              </div>
            )}

            <button
              onClick={handleSaveSettings}
              disabled={loading}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 font-medium disabled:bg-gray-300 flex items-center"
            >
              <Save size={18} className="mr-2" />
              {loading ? 'Saving...' : 'Save Promo Settings'}
            </button>
          </div>
        </div>

        {/* Vendor Participation Agreement */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Vendor Participation Agreement</h2>
          <p className="text-sm text-gray-600 mb-4">
            This agreement is shown to vendors during onboarding
          </p>
          
          <textarea
            value={vendorAgreement}
            onChange={(e) => setVendorAgreement(e.target.value)}
            rows={12}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm"
          />

          <button
            onClick={() => handleSaveAgreement('vendor_participation', vendorAgreement)}
            disabled={loading}
            className="mt-4 bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 font-medium disabled:bg-gray-300 flex items-center"
          >
            <Save size={18} className="mr-2" />
            {loading ? 'Saving...' : 'Save Vendor Agreement'}
          </button>
        </div>

        {/* Service Agreement */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Service Agreement (Customer ↔ Vendor)</h2>
          <p className="text-sm text-gray-600 mb-4">
            This agreement is shown when a booking is accepted and before it becomes active
          </p>
          
          <textarea
            value={serviceAgreement}
            onChange={(e) => setServiceAgreement(e.target.value)}
            rows={12}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm"
          />

          <button
            onClick={() => handleSaveAgreement('service_agreement', serviceAgreement)}
            disabled={loading}
            className="mt-4 bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 font-medium disabled:bg-gray-300 flex items-center"
          >
            <Save size={18} className="mr-2" />
            {loading ? 'Saving...' : 'Save Service Agreement'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlatformSettings;
